import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {UpdatePosterDataProps} from '@Components/poster-editor/poster-editor-reducer';
import {updateItemData} from '@Components/poster-editor/poster-editor-reducer';
import {onPosterItemsModified, updateArePosterItemsMoving, updateArePosterItemsRotating, updateArePosterItemsScaling} from '@Components/poster-editor/poster-editor-reducer';
import {
  restoreInitialData,
  setSelectedAudioItemUID,
  updateAudioItemIsPlaying,
  updateCurrentTime,
  updateDidPosterRecentlySave,
  updateDidPosterSaveFailed,
  updateIsPosterSaving,
  updateHasRedo,
  updateHasUndo,
  updateIsPlaying,
  updatePosterData,
} from '@Components/poster-editor/poster-editor-reducer';
import type {DeepPartial} from '@/global';
import type {ItemObject} from '../items/item/item.types';

export class PosterRedux {
  private readonly poster: Poster;

  constructor(poster: Poster) {
    this.poster = poster;
    this.poster.on('time:updated', (time: number) => {
      window.PMW.redux.store.dispatch(updateCurrentTime(time));
    });
  }

  public onDispose = (): void => {
    window.PMW.redux.store.dispatch(restoreInitialData());
  };

  public updateReduxData = (): void => {
    window.PMW.redux.store.dispatch(updatePosterData(this.getPosterDataForRedux()));
  };

  public updateReduxItemData = (pageHashId: string, itemHashId: string, obj: DeepPartial<ItemObject>): void => {
    // Disabled for now as it triggers rerender of all editing options
    window.PMW.redux.store.dispatch(
      updateItemData({
        pageHashId,
        itemHashId,
        obj,
      })
    );
  };

  public updateHasUndoInRedux = (): void => {
    window.PMW.redux.store.dispatch(updateHasUndo(this.poster.history.hasUndo()));
  };

  public updateDidPosterSaveFailed = (val: boolean): void => {
    window.PMW.redux.store.dispatch(updateDidPosterSaveFailed(val));
  };

  public setPosterItemsModificationState = (modificationType: 'scaling' | 'moving' | 'rotating' | false, pageHashedId: string): void => {
    switch (modificationType) {
      case 'moving':
        window.PMW.redux.store.dispatch(updateArePosterItemsMoving({pageHashedId: pageHashedId, isModifying: true}));
        return;

      case 'scaling':
        window.PMW.redux.store.dispatch(updateArePosterItemsScaling({pageHashedId: pageHashedId, isModifying: true}));
        return;

      case 'rotating':
        window.PMW.redux.store.dispatch(updateArePosterItemsRotating({pageHashedId: pageHashedId, isModifying: true}));
        return;

      case false:
        window.PMW.redux.store.dispatch(onPosterItemsModified(pageHashedId));
        return;
    }
  };

  public updateIsPosterSaving = (val: boolean): void => {
    window.PMW.redux.store.dispatch(updateIsPosterSaving(val));
  };

  public updateDidPosterRecentlySave = (val: boolean): void => {
    window.PMW.redux.store.dispatch(updateDidPosterRecentlySave(val));
  };

  public updateHasRedoInRedux = (): void => {
    window.PMW.redux.store.dispatch(updateHasRedo(this.poster.history.hasRedo()));
  };

  public updateIsPlayingInRedux = (): void => {
    window.PMW.redux.store.dispatch(updateIsPlaying(this.poster.isPlaying()));
  };

  public updateAudioItemIsPlayingInRedux = (audioItemUID: string, isPlaying: boolean): void => {
    window.PMW.redux.store.dispatch(
      updateAudioItemIsPlaying({
        audioItemUID,
        isPlaying,
      })
    );
  };

  public updateSelectedAudioItemUID = (): void => {
    window.PMW.redux.store.dispatch(setSelectedAudioItemUID(this.poster.audioClips.selectedAudioItemUID));
  };

  public updateUndoRedoStates(): void {
    this.updateHasUndoInRedux();
    this.updateHasRedoInRedux();
  }

  private getPosterDataForRedux = (): UpdatePosterDataProps => {
    return {
      isVideo: this.poster.isVideo(),
      posterObject: this.poster.toObject(),
      currentPageId: this.poster.getCurrentPage().hashedID,
      isPosterVideo: this.poster.isVideo(),
      hasUnsavedChanges: this.poster.savePoster.hasUnsavedChanges(),
    };
  };
}
